// =============================================================================
// HopShareModal.tsx — HOP Slice 3 (Stage C). The project's CLIENT HOP PORTAL
// MANAGEMENT view: the one persistent link (copy / open / extend / revoke) and
// the timeline of progress updates already sent.
//
// Picking + sending photos now happens on the pack category page ("Share to
// client" mode → shareRelease.ts), where photos are already grouped by
// sector/subcategory and the labels get snapshotted at send time. This modal is
// no longer a picker — just the link + what's been sent.
//
// Public page = /hop-view/:token.
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X, Share2, Link2, Copy, Check, CalendarClock, ExternalLink, Trash2,
  CheckCircle2, Camera, FileText, AlertTriangle, Send,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button } from "@/components/ui";
import { format, parseISO } from "date-fns";
import { sendDocuments } from "./shareRelease";

interface Props { projectId: string; onClose: () => void; }

const EXPIRY_DAYS = 20;

export default function HopShareModal({ projectId, onClose }: Props) {
  const user = useRequireAuth();
  const [copied, setCopied] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [docsBusy, setDocsBusy] = useState(false);
  const [docsErr, setDocsErr] = useState<string | null>(null);

  const { data: project } = useQuery({
    queryKey: ["hop_share_project", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_code, project_name, clients ( legal_name )").eq("project_id", projectId).single();
      return data as any;
    },
  });

  // The live (non-revoked, unexpired) share for this project, if one exists.
  const { data: share, refetch: refetchShare } = useQuery({
    queryKey: ["hop_share_row", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("hop_shares")
        .select("share_id, token, title, expires_at, revoked_at, view_count")
        .eq("project_id", projectId).is("revoked_at", null)
        .order("created_at", { ascending: false }).limit(1);
      const row = (data ?? [])[0] as any;
      if (!row) return null;
      const live = !row.expires_at || new Date(row.expires_at).getTime() > Date.now();
      return live ? row : null;
    },
  });

  // Timeline of progress updates sent + item counts.
  const { data: batchInfo, refetch: refetchBatches } = useQuery({
    queryKey: ["hop_share_batches", share?.share_id],
    enabled: !!share?.share_id,
    queryFn: async () => {
      const { data: bs } = await supabase.from("hop_share_batches")
        .select("batch_id, title, created_at")
        .eq("share_id", share!.share_id)
        .is("revoked_at", null)   // T-417 — retracted batches drop off the timeline too
        .order("created_at", { ascending: false });
      const batches = (bs ?? []) as any[];
      const ids = batches.map(b => b.batch_id);
      const countByBatch = new Map<string, number>();
      const sharedDocs = new Set<string>();
      if (ids.length) {
        const { data: ph } = await supabase.from("hop_share_photos").select("batch_id").in("batch_id", ids);
        for (const p of (ph ?? []) as any[]) countByBatch.set(p.batch_id, (countByBatch.get(p.batch_id) ?? 0) + 1);
        const { data: dc } = await supabase.from("hop_share_docs").select("batch_id, upload_id").in("batch_id", ids);
        for (const d of (dc ?? []) as any[]) { countByBatch.set(d.batch_id, (countByBatch.get(d.batch_id) ?? 0) + 1); sharedDocs.add(d.upload_id); }
      }
      return { batches: batches.map(b => ({ ...b, count: countByBatch.get(b.batch_id) ?? 0 })), sharedDocs };
    },
  });

  // Acceptance documents available to share (deliverables library, project).
  const { data: acceptanceDocs = [] } = useQuery({
    queryKey: ["hop_share_docs_avail", projectId],
    queryFn: async () => {
      const { data } = await supabase.from("document_library_uploads")
        .select("upload_id, file_name, version_label, is_archived, line:line_id ( display_name, category:category_id ( code ) )")
        .eq("project_id", projectId).eq("is_archived", false)
        .order("uploaded_at", { ascending: false });
      return ((data ?? []) as any[]).filter(u => u.line?.category?.code === "acceptance");
    },
  });
  const alreadySharedDocs: Set<string> = (batchInfo as any)?.sharedDocs ?? new Set();

  const link = share ? `${window.location.origin}/hop-view/${share.token}` : null;
  const daysLeft = share?.expires_at ? Math.ceil((new Date(share.expires_at).getTime() - Date.now()) / 86400000) : null;

  async function copyLink() {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  }
  async function extend() {
    if (!share) return;
    await supabase.from("hop_shares").update({ expires_at: new Date(Date.now() + EXPIRY_DAYS * 86400000).toISOString() }).eq("share_id", share.share_id);
    refetchShare();
  }
  async function revoke() {
    if (!share || !confirm("Revoke this client link? The client can no longer open it.")) return;
    await supabase.from("hop_shares").update({ revoked_at: new Date().toISOString() }).eq("share_id", share.share_id);
    refetchShare();
  }

  // T-417 — batch-level controls: retract a whole update, or pull one photo.
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const { data: openBatchPhotos = [], refetch: refetchBatchPhotos } = useQuery({
    queryKey: ["hop_share_batch_photos", openBatchId],
    enabled: !!openBatchId,
    queryFn: async () => {
      const { data } = await supabase.from("hop_share_photos")
        .select("asset_id, category_label, instance_label, subcat_label")
        .eq("batch_id", openBatchId!);
      return (data ?? []) as any[];
    },
  });
  async function retractBatch(batchId: string, title: string) {
    if (!confirm(`Retract "${title || "this update"}" from the client portal? It disappears from their link immediately (kept on record here). Re-share the right photos from the pack afterwards.`)) return;
    await supabase.from("hop_share_batches").update({ revoked_at: new Date().toISOString() }).eq("batch_id", batchId);
    setOpenBatchId(null);
    refetchBatches();
  }
  async function removeBatchPhoto(batchId: string, assetId: string) {
    await supabase.from("hop_share_photos").delete().eq("batch_id", batchId).eq("asset_id", assetId);
    refetchBatchPhotos();
    refetchBatches();
  }

  const defaultTitle = project ? `${project.project_code} — HOP Photos` : "HOP Photos";
  const pickableDocs = acceptanceDocs.filter((d: any) => !alreadySharedDocs.has(d.upload_id));
  const nDocsSel = [...selectedDocs].filter(id => !alreadySharedDocs.has(id)).length;

  async function sendDocs() {
    setDocsBusy(true); setDocsErr(null);
    try {
      const ids = [...selectedDocs].filter(id => !alreadySharedDocs.has(id));
      await sendDocuments({ projectId, userId: user?.user_id ?? null, uploadIds: ids, defaultTitle });
      setSelectedDocs(new Set());
      await refetchShare(); await refetchBatches();
    } catch (e: any) { setDocsErr(e?.message ?? "Couldn't send the documents."); }
    finally { setDocsBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-2xl md:rounded-lg max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Share2 size={18} className="text-accent flex-shrink-0" />
            <h3 className="text-[16px] font-semibold text-text truncate">Client HOP portal</h3>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Project */}
          <div className="bg-ink-raised border border-line rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Project</div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-accent text-[14px]">{project?.project_code ?? "…"}</span>
              <span className="text-[13px] text-text">{project?.project_name}</span>
            </div>
            {project?.clients?.legal_name && <div className="text-[12px] text-text-muted mt-0.5">Client: {project.clients.legal_name}</div>}
          </div>

          {/* The link */}
          {link ? (
            <div className={`rounded-md border p-3 ${daysLeft != null && daysLeft <= 5 ? "bg-warn/10 border-warn/40" : "bg-ink-raised border-line"}`}>
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 flex items-center gap-1.5"><Link2 size={11} /> Client link</div>
              <input readOnly value={link} onFocus={e => e.currentTarget.select()} className="w-full px-3 py-2 bg-ink border border-line rounded text-[12px] text-text font-mono" />
              <div className="flex items-center gap-2 mt-2 flex-wrap text-[12px]">
                <Button size="sm" icon={copied ? Check : Copy} onClick={copyLink}>{copied ? "Copied" : "Copy link"}</Button>
                <Button size="sm" variant="secondary" icon={ExternalLink} onClick={() => window.open(link, "_blank", "noopener")}>Open</Button>
                <button onClick={extend} className="text-text-muted hover:text-accent inline-flex items-center gap-1"><CalendarClock size={12} /> +{EXPIRY_DAYS}d</button>
                <button onClick={revoke} className="text-red-400 hover:underline inline-flex items-center gap-1"><Trash2 size={12} /> Revoke</button>
                <span className={`ml-auto ${daysLeft != null && daysLeft <= 5 ? "text-warn" : "text-text-muted"}`}>
                  {daysLeft != null ? `expires in ${daysLeft}d` : "live"}{typeof share?.view_count === "number" ? ` · ${share.view_count} views` : ""}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-line bg-ink-raised p-4 text-[12px] text-text-muted">
              No client link yet. Open a HOP pack → a category → tap <span className="text-text">Share to client</span>, tick the photos and send. The link is created automatically the first time — you send it to the client once.
            </div>
          )}

          {/* Progress updates already sent */}
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-2">Progress updates sent</div>
            <div className="space-y-1.5">
              {(batchInfo?.batches ?? []).map((b: any) => (
                <div key={b.batch_id} className="rounded-md border border-line bg-ink-raised">
                  <div className="px-3 py-2 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => setOpenBatchId(openBatchId === b.batch_id ? null : b.batch_id)}
                      title="Show the photos in this update"
                    >
                      <div className="text-[13px] text-text truncate">{b.title || "Progress update"}</div>
                      <div className="text-[11px] text-text-muted">{format(parseISO(b.created_at), "EEE dd MMM")} · {b.count} item{b.count === 1 ? "" : "s"} · tap to manage</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => retractBatch(b.batch_id, b.title)}
                      className="text-[11px] px-2 py-1 rounded border border-line text-text-muted hover:text-danger hover:border-danger/50 flex-shrink-0"
                      title="Pull this whole update off the client portal"
                    >
                      Retract
                    </button>
                    <CheckCircle2 size={14} className="text-ok flex-shrink-0" />
                  </div>
                  {/* T-417 — expanded: pull individual photos from the live batch */}
                  {openBatchId === b.batch_id && (
                    <div className="px-3 pb-2 space-y-1 border-t border-line/50 pt-2">
                      {openBatchPhotos.length === 0 ? (
                        <div className="text-[11px] text-text-muted italic">No photos in this update (documents only, or all pulled).</div>
                      ) : openBatchPhotos.map((p: any) => (
                        <div key={p.asset_id} className="flex items-center justify-between gap-2 text-[12px]">
                          <span className="text-text-muted truncate">
                            {[p.category_label, p.instance_label, p.subcat_label].filter(Boolean).join(" · ") || "Photo"}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeBatchPhoto(b.batch_id, p.asset_id)}
                            className="text-text-muted hover:text-danger p-0.5 flex-shrink-0"
                            title="Pull this photo off the client portal"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(batchInfo?.batches?.length ?? 0) === 0 && <div className="text-[12px] text-text-muted italic px-1">Nothing sent yet.</div>}
            </div>
          </div>

          {/* Acceptance documents — share deliverable PDFs (project-level) */}
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-2 flex items-center gap-1.5"><FileText size={12} /> Acceptance documents</div>
            {acceptanceDocs.length === 0 ? (
              <div className="text-[12px] text-text-muted italic px-1">No acceptance documents on this project yet. Upload them on the pack's deliverables, then share them here.</div>
            ) : (
              <div className="space-y-1.5">
                {acceptanceDocs.map((d: any) => {
                  const shared = alreadySharedDocs.has(d.upload_id);
                  const sel = selectedDocs.has(d.upload_id);
                  return (
                    <button key={d.upload_id} type="button" disabled={shared}
                      onClick={() => setSelectedDocs(prev => { const n = new Set(prev); sel ? n.delete(d.upload_id) : n.add(d.upload_id); return n; })}
                      className={`w-full text-left px-3 py-2 rounded-md border flex items-center gap-3 transition-colors ${shared ? "border-line opacity-50" : sel ? "bg-accent/10 border-accent" : "bg-ink-raised border-line hover:border-accent/50"}`}>
                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${sel && !shared ? "bg-accent border-accent" : "border-line"}`}>{sel && !shared && <Check size={12} className="text-white" />}</div>
                      <FileText size={15} className="text-accent flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-text truncate">{d.line?.display_name ?? "Document"}{d.version_label ? ` · ${d.version_label}` : ""}</div>
                        <div className="text-[11px] text-text-muted font-mono truncate">{d.file_name}</div>
                      </div>
                      {shared && <span className="text-[10px] text-white bg-ok/80 rounded px-1.5 py-0.5 flex-shrink-0">Shared</span>}
                    </button>
                  );
                })}
                {docsErr && <div className="p-2.5 rounded bg-red-900/30 border border-red-900/50 text-[12px] text-red-200 flex items-start gap-2"><AlertTriangle size={12} className="mt-0.5" /><span>{docsErr}</span></div>}
                {pickableDocs.length > 0 && (
                  <div className="flex justify-end pt-1">
                    <Button size="sm" icon={Send} onClick={sendDocs} disabled={docsBusy || nDocsSel === 0}>
                      {docsBusy ? "Sending…" : `Send ${nDocsSel || ""} document${nDocsSel === 1 ? "" : "s"}`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* How to send photos */}
          <div className="rounded-md border border-dashed border-line p-3 text-[12px] text-text-muted flex items-start gap-2">
            <Camera size={14} className="mt-0.5 flex-shrink-0 text-accent" />
            <span>To send <span className="text-text">photos</span>, open a HOP pack → a category → <span className="text-text">Share to client</span>. Pick per sector/subcategory and send as a progress update — only what you tick is shared.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
